import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import { getCookingSessions } from '../services/api';
import { Heading, Text, Modal } from './ui';

const CookingCalendar = ({ isOpen = true, onClose = () => {}, kernelId = null, sessions = [], scheduledMeals = [], onSessionClick = () => {}, onSessionDelete = () => {}, onScheduledMealDelete = () => {} }) => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [internalSessions, setInternalSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Calendar state
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedSession, setSelectedSession] = useState(null);
    const [sessionDetailModalOpen, setSessionDetailModalOpen] = useState(false);
    const [mobileWeekExpanded, setMobileWeekExpanded] = useState(null);
    const [selectedDaySessions, setSelectedDaySessions] = useState([]);
    const [selectedDayMeals, setSelectedDayMeals] = useState([]);
    const [selectedDayDate, setSelectedDayDate] = useState(null);
    const [daySessionsModalOpen, setDaySessionsModalOpen] = useState(false);

    useEffect(() => {
        if (sessions.length > 0) {
            // Use passed sessions
            setInternalSessions(sessions);
            setLoading(false);
        } else if (isOpen && user) {
            fetchSessions();
        }
    }, [sessions, isOpen, user, kernelId]);

    const fetchSessions = async () => {
        try {
            setLoading(true);
            const token = await user.getIdToken();
            const data = await getCookingSessions(token);
            
            // Filter sessions, optionally by kernel_id
            const filteredSessions = data.filter(session => 
                (!kernelId || session.kernel_id === kernelId)
            );
            
            setInternalSessions(filteredSessions);
        } catch (err) {
            console.error('Error fetching sessions:', err);
            setError('Failed to load cooking sessions');
        } finally {
            setLoading(false);
        }
    };

    // Calendar helper functions
    const getFirstDayOfMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth(), 1);
    };

    const getLastDayOfMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    };

    const getDaysInMonth = (date) => {
        return getLastDayOfMonth(date).getDate();
    };

    const getStartingDayOfWeek = (date) => {
        return getFirstDayOfMonth(date).getDay();
    };

    const isSameDate = (date1, date2) => {
        return date1.getFullYear() === date2.getFullYear() &&
               date1.getMonth() === date2.getMonth() &&
               date1.getDate() === date2.getDate();
    };

    const getSessionsForDate = (date) => {
        return internalSessions.filter(session => {
            const sessionDate = new Date(session.created_at);
            return isSameDate(date, sessionDate);
        });
    };

    const getScheduledMealsForDate = (date) => {
        if (!scheduledMeals || !Array.isArray(scheduledMeals) || scheduledMeals.length === 0) return [];
        return scheduledMeals.filter(meal => {
            if (!meal || !meal.scheduled_date) return false;
            const mealDate = new Date(meal.scheduled_date);
            return isSameDate(date, mealDate);
        });
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    const formatDuration = (seconds) => {
        if (!seconds) return 'N/A';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        if (mins > 0) {
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        }
        return `0:${secs.toString().padStart(2, '0')}`;
    };

    const navigateMonth = (direction) => {
        setCurrentDate(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(newDate.getMonth() + direction);
            return newDate;
        });
    };

    const handleSessionClick = (session) => {
        setSelectedSession(session);
        setSessionDetailModalOpen(true);
    };

    const handleDayClick = (date, daySessions, dayMeals = []) => {
        if (daySessions.length > 0 || dayMeals.length > 0) {
            setSelectedDaySessions(daySessions);
            setSelectedDayMeals(dayMeals);
            setSelectedDayDate(date);
            setDaySessionsModalOpen(true);
        }
    };

    const renderMobileCalendar = () => {
        const daysInMonth = getDaysInMonth(currentDate);
        const startingDayOfWeek = getStartingDayOfWeek(currentDate);
        const today = new Date();
        
        // Calculate weeks
        const totalCells = startingDayOfWeek + daysInMonth;
        const weeksNeeded = Math.ceil(totalCells / 7);
        
        const weeks = [];
        
        for (let week = 0; week < weeksNeeded; week++) {
            const weekStart = week * 7;
            const weekEnd = Math.min(weekStart + 7, totalCells);
            const weekDays = [];
            
            for (let i = weekStart; i < weekEnd; i++) {
                if (i < startingDayOfWeek) {
                    // Empty cell before month starts
                    weekDays.push(
                        <div key={`empty-${i}`} className="bg-neutral-50 border border-neutral-200 min-h-[120px]"></div>
                    );
                } else {
                    // Actual day
                    const day = i - startingDayOfWeek + 1;
                    const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                    const daySessions = getSessionsForDate(date);
                    const dayMeals = getScheduledMealsForDate(date);
                    const totalItems = daySessions.length + dayMeals.length;
                    const isToday = isSameDate(date, today);
                    
                    weekDays.push(
                        <div 
                            key={day} 
                            className={`p-1 border border-neutral-200 min-h-[120px] ${isToday ? 'bg-neutral-100' : 'bg-neutral-0'}`}
                        >
                            <div className="flex justify-between items-start mb-1">
                                <span className={`text-xs font-mono ${isToday ? 'font-bold' : ''}`}>
                                    {day}
                                </span>
                                {totalItems > 0 && (
                                    <span className="text-xs bg-neutral-900 text-neutral-0 px-1 rounded">
                                        {totalItems}
                                    </span>
                                )}
                            </div>
                            
                            {/* Scheduled meals */}
                            {dayMeals.length > 0 && (
                                <div className="space-y-1 mb-1">
                                    {dayMeals.map((meal) => (
                                        <div
                                            key={meal.id}
                                            className="border border-[#1b263b] rounded-sm p-1 bg-[#1b263b] bg-opacity-10"
                                        >
                                            <div className="w-full text-left text-xs">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-neutral-900 font-medium truncate">
                                                        {meal.recipe_name}
                                                    </span>
                                                    <span className="font-mono text-xs uppercase tracking-wider bg-[#1b263b] text-white px-1 ml-1 text-[10px]">
                                                        SCH
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            {daySessions.length > 0 && (
                                <div className="space-y-1">
                                    {/* First session with photo */}
                                    <div className={`border border-neutral-200 rounded-sm p-1 ${
                                        daySessions[0].was_completed ? 'bg-neutral-900' : 'bg-neutral-0'
                                    }`}>
                                        <div
                                            className="w-full text-left text-xs cursor-pointer hover:opacity-80 transition-opacity"
                                            onClick={() => handleSessionClick(daySessions[0])}
                                        >
                                            <div className="w-full">
                                                <div className="w-full mb-1">
                                                    {(() => {
                                                        let imageUrl = null;
                                                        if (daySessions[0].in_progress_photos && daySessions[0].in_progress_photos.length > 0) {
                                                            imageUrl = daySessions[0].in_progress_photos[0];
                                                        } else if (daySessions[0].recipe_images && daySessions[0].recipe_images.length > 0) {
                                                            imageUrl = daySessions[0].recipe_images[0];
                                                        } else if (daySessions[0].base_image_url) {
                                                            imageUrl = daySessions[0].base_image_url;
                                                        }
                                                        
                                                        return imageUrl ? (
                                                            <img 
                                                                src={imageUrl} 
                                                                alt="Recipe" 
                                                                className="w-full h-24 object-cover rounded-sm"
                                                            />
                                                        ) : (
                                                            <div className={`w-full h-24 rounded-sm flex items-center justify-center text-lg font-mono ${
                                                                daySessions[0].was_completed 
                                                                    ? 'bg-neutral-700 text-neutral-0' 
                                                                    : daySessions[0].is_paused
                                                                    ? 'bg-neutral-900 text-neutral-0'
                                                                    : 'bg-neutral-200 text-neutral-900'
                                                            }`}>
                                                                {daySessions[0].recipe_name ? daySessions[0].recipe_name.charAt(0).toUpperCase() : 
                                                                 daySessions[0].kernel_title ? daySessions[0].kernel_title.charAt(0).toUpperCase() : '?'}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                                <span className={`text-xs truncate block ${
                                                    daySessions[0].was_completed ? 'text-neutral-0' : 'text-neutral-900'
                                                }`}>
                                                    {daySessions[0].recipe_name || daySessions[0].kernel_title || 'Custom Recipe'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* All additional sessions with photos */}
                                    {daySessions.slice(1).map((session, index) => (
                                        <div
                                            key={session.id}
                                            className={`w-full text-left cursor-pointer hover:opacity-80 transition-opacity ${
                                                session.was_completed 
                                                    ? 'bg-neutral-900 text-neutral-0' 
                                                    : session.is_paused
                                                    ? 'bg-neutral-900 text-neutral-0'
                                                    : 'bg-neutral-50 text-neutral-900 hairline'
                                            } border border-neutral-200 rounded-sm p-1`}
                                            onClick={() => handleSessionClick(session)}
                                        >
                                            <div className="w-full">
                                                <div className="w-full mb-1">
                                                    {(() => {
                                                        let imageUrl = null;
                                                        if (session.in_progress_photos && session.in_progress_photos.length > 0) {
                                                            imageUrl = session.in_progress_photos[0];
                                                        } else if (session.recipe_images && session.recipe_images.length > 0) {
                                                            imageUrl = session.recipe_images[0];
                                                        } else if (session.base_image_url) {
                                                            imageUrl = session.base_image_url;
                                                        }
                                                        
                                                        return imageUrl ? (
                                                            <img 
                                                                src={imageUrl} 
                                                                alt="Recipe" 
                                                                className="w-full h-24 object-cover rounded-sm"
                                                            />
                                                        ) : (
                                                            <div className={`w-full h-24 rounded-sm flex items-center justify-center text-lg font-mono ${
                                                                session.was_completed 
                                                                    ? 'bg-neutral-700 text-neutral-0' 
                                                                    : session.is_paused
                                                                    ? 'bg-neutral-900 text-neutral-0'
                                                                    : 'bg-neutral-200 text-neutral-900'
                                                            }`}>
                                                                {session.recipe_name ? session.recipe_name.charAt(0).toUpperCase() : 
                                                                 session.kernel_title ? session.kernel_title.charAt(0).toUpperCase() : '?'}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                                <span className={`text-xs truncate block ${
                                                    session.was_completed ? 'text-neutral-0' : 'text-neutral-900'
                                                }`}>
                                                    {session.recipe_name || session.kernel_title || 'Custom Recipe'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                }
            }
            
            weeks.push(
                <div key={week} className="mb-2">
                    <div 
                        className="flex items-center justify-between p-2 bg-neutral-50 border border-neutral-200 cursor-pointer hover:bg-neutral-100 transition-colors"
                        onClick={() => setMobileWeekExpanded(mobileWeekExpanded === week ? null : week)}
                    >
                        <span className="text-sm font-mono uppercase tracking-wider">
                            Week {week + 1}
                        </span>
                        <span className="text-xs text-neutral-600">
                            {mobileWeekExpanded === week ? '▼' : '▶'}
                        </span>
                    </div>
                    {mobileWeekExpanded === week && (
                        <div className="grid grid-cols-7 gap-0 border border-neutral-200">
                            {weekDays}
                        </div>
                    )}
                </div>
            );
        }
        
        return weeks;
    };

    const renderCalendarGrid = () => {
        const daysInMonth = getDaysInMonth(currentDate);
        const startingDayOfWeek = getStartingDayOfWeek(currentDate);
        const today = new Date();
        
        const days = [];
        
        // Empty cells for days before the first day of the month
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(
                <div key={`empty-${i}`} className="bg-neutral-50 border border-neutral-200"></div>
            );
        }
        
        // Days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
            const daySessions = getSessionsForDate(date);
            const dayMeals = getScheduledMealsForDate(date);
            const totalItems = daySessions.length + dayMeals.length;
            const isToday = isSameDate(date, today);
            
            days.push(
                <div 
                    key={day} 
                    className={`p-1 border border-neutral-200 ${isToday ? 'bg-neutral-100' : 'bg-neutral-0'}`}
                >
                    <div 
                        className="flex justify-between items-start mb-1"
                        onClick={() => totalItems > 1 && handleDayClick(date, daySessions, dayMeals)}
                        style={{ cursor: totalItems > 1 ? 'pointer' : 'default' }}
                    >
                        <span className={`text-xs font-mono ${isToday ? 'font-bold' : ''}`}>
                            {day}
                        </span>
                        {totalItems > 0 && (
                            <span className="text-xs bg-neutral-900 text-neutral-0 px-1 rounded">
                                {totalItems}
                            </span>
                        )}
                    </div>
                    
                    <div className="space-y-1">
                        {/* Scheduled meals first */}
                        {dayMeals.length > 0 && dayMeals.map((meal) => (
                            <div
                                key={meal.id}
                                className="border border-[#1b263b] rounded-sm p-1 bg-[#1b263b] bg-opacity-10"
                            >
                                <div className="w-full text-left text-xs">
                                    <div className="flex items-center justify-between">
                                        <span className="text-neutral-900 font-medium truncate">
                                            {meal.recipe_name}
                                        </span>
                                        <span className="font-mono text-xs uppercase tracking-wider bg-[#1b263b] text-white px-1 ml-1">
                                            SCHEDULED
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                        
                        {/* First session with photo - grouped in its own container */}
                        {daySessions.length > 0 && (
                            <div className={`border border-neutral-200 rounded-sm p-1 ${
                                daySessions[0].was_completed ? 'bg-neutral-900' : 'bg-neutral-0'
                            }`}>
                                <div
                                    key={daySessions[0].id}
                                    className="w-full text-left text-xs cursor-pointer hover:opacity-80 transition-opacity"
                                    title={`${daySessions[0].recipe_name || daySessions[0].kernel_title || 'Custom Recipe'} - ${daySessions[0].was_completed ? 'COMPLETED' : daySessions[0].is_paused ? 'PAUSED' : 'IN PROGRESS'}`}
                                    onClick={() => handleSessionClick(daySessions[0])}
                                >
                                    <div className="w-full">
                                        <div className="w-full mb-1">
                                            {(() => {
                                                // Priority: in-progress photos > recipe images > base images > default
                                                let imageUrl = null;
                                                
                                                // Check for in-progress photos first
                                                if (daySessions[0].in_progress_photos && daySessions[0].in_progress_photos.length > 0) {
                                                    imageUrl = daySessions[0].in_progress_photos[0];
                                                }
                                                // Check for recipe images
                                                else if (daySessions[0].recipe_images && daySessions[0].recipe_images.length > 0) {
                                                    imageUrl = daySessions[0].recipe_images[0];
                                                }
                                                // Check for base images
                                                else if (daySessions[0].base_image_url) {
                                                    imageUrl = daySessions[0].base_image_url;
                                                }
                                                
                                                return imageUrl ? (
                                                    <img 
                                                        src={imageUrl} 
                                                        alt="Recipe" 
                                                        className="w-full h-20 object-cover rounded-sm"
                                                    />
                                                ) : (
                                                    <div className={`w-full h-20 rounded-sm flex items-center justify-center text-2xl font-mono ${
                                                        daySessions[0].was_completed 
                                                            ? 'bg-neutral-700 text-neutral-0' 
                                                            : daySessions[0].is_paused
                                                            ? 'bg-neutral-900 text-neutral-0'
                                                            : 'bg-neutral-200 text-neutral-900'
                                                    }`}>
                                                        {daySessions[0].recipe_name ? daySessions[0].recipe_name.charAt(0).toUpperCase() : 
                                                         daySessions[0].kernel_title ? daySessions[0].kernel_title.charAt(0).toUpperCase() : '?'}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        <span className={`text-xs truncate block ${
                                            daySessions[0].was_completed ? 'text-neutral-0' : 'text-neutral-900'
                                        }`}>
                                            {daySessions[0].recipe_name || daySessions[0].kernel_title || 'Custom Recipe'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Additional sessions (titles only) - shown below the first session */}
                        {daySessions.slice(1, 3).map((session, index) => (
                            <div
                                key={session.id}
                                className={`w-full text-left px-1 py-0.5 text-xs truncate cursor-pointer hover:opacity-80 transition-opacity ${
                                    session.was_completed 
                                        ? 'bg-neutral-900 text-neutral-0' 
                                        : session.is_paused
                                        ? 'bg-neutral-900 text-neutral-0'
                                        : 'bg-neutral-50 text-neutral-900 hairline'
                                }`}
                                title={`${session.recipe_name || session.kernel_title || 'Custom Recipe'} - ${session.was_completed ? 'COMPLETED' : session.is_paused ? 'PAUSED' : 'IN PROGRESS'}`}
                                onClick={() => handleSessionClick(session)}
                            >
                                <span className="truncate">
                                    {session.recipe_name || session.kernel_title || 'Custom Recipe'}
                                </span>
                            </div>
                        ))}
                        
                        {daySessions.length > 3 && (
                            <div className="text-xs text-neutral-900 opacity-60 px-1">
                                +{daySessions.length - 3} more
                            </div>
                        )}
                    </div>
                </div>
            );
        }
        
        return days;
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="w-full">
                <div className="bg-neutral-0 hairline minimal-padding">

                {loading ? (
                    <div className="text-center py-8">
                        <Text>Loading cooking sessions...</Text>
                    </div>
                ) : error ? (
                    <div className="text-center py-8">
                        <Text className="text-red-600">{error}</Text>
                    </div>
                ) : (
                    <>
                        {/* Calendar Header */}
                        <div className="flex items-center justify-between mb-6">
                            <button
                                onClick={() => navigateMonth(-1)}
                                className="w-8 h-8 flex items-center justify-center bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50 transition-colors"
                            >
                                ‹
                            </button>
                            
                            <Heading level={3} className="font-mono uppercase tracking-wider">
                                {currentDate.toLocaleDateString('en-US', { 
                                    year: 'numeric', 
                                    month: 'long' 
                                })}
                            </Heading>
                            
                            <button
                                onClick={() => navigateMonth(1)}
                                className="w-8 h-8 flex items-center justify-center bg-neutral-0 text-neutral-900 hairline hover:bg-neutral-50 transition-colors"
                            >
                                ›
                            </button>
                        </div>

                        {/* Days of week header - Desktop only */}
                        <div className="hidden md:grid grid-cols-7 gap-0 mb-2">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                <div key={day} className="h-8 flex items-center justify-center bg-neutral-50 border border-neutral-200">
                                    <Text className="font-mono text-xs uppercase tracking-wider">
                                        {day}
                                    </Text>
                                </div>
                            ))}
                        </div>

                        {/* Calendar Grid */}
                        {/* Desktop Calendar */}
                        <div className="hidden md:block">
                            <div className="grid grid-cols-7 gap-0 border border-neutral-200" style={{ gridTemplateRows: 'repeat(6, minmax(80px, auto))' }}>
                                {renderCalendarGrid()}
                            </div>
                        </div>
                        
                        {/* Mobile Calendar */}
                        <div className="block md:hidden">
                            {renderMobileCalendar()}
                        </div>

                        {/* Summary */}
                        <div className="mt-6 pt-4 border-t border-neutral-200">
                            <Text className="text-sm text-neutral-900 opacity-60">
                                {internalSessions.length} cooking session{internalSessions.length !== 1 ? 's' : ''} 
                                {scheduledMeals && scheduledMeals.length > 0 && ` • ${scheduledMeals.length} scheduled meal${scheduledMeals.length !== 1 ? 's' : ''}`}
                                {kernelId ? ' for this technique' : ' total'}
                            </Text>
                        </div>
                    </>
                )}
            </div>
        </div>

            {/* Day Sessions Modal */}
            <Modal
                isOpen={daySessionsModalOpen}
                onClose={() => {
                    setDaySessionsModalOpen(false);
                    setSelectedDaySessions([]);
                    setSelectedDayMeals([]);
                    setSelectedDayDate(null);
                }}
                title={`Activity for ${selectedDayDate ? formatDate(selectedDayDate) : ''}`}
                maxWidth="max-w-3xl"
            >
                <div className="vspace">
                    {/* Scheduled Meals Section */}
                    {selectedDayMeals && Array.isArray(selectedDayMeals) && selectedDayMeals.length > 0 && (
                        <>
                            <Heading level={3} className="mb-4 font-mono uppercase tracking-wider text-sm">
                                SCHEDULED MEALS
                            </Heading>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                                {selectedDayMeals.map((meal) => (
                                    <div key={meal.id} className="border border-[#1b263b] p-4 bg-neutral-0">
                                        <div className="mb-3">
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="font-mono text-xs uppercase tracking-wider bg-[#1b263b] text-white px-2 py-1">
                                                    SCHEDULED
                                                </span>
                                                {onScheduledMealDelete && (
                                                    <button
                                                        onClick={async () => {
                                                            if (window.confirm('Remove this scheduled meal?')) {
                                                                try {
                                                                    await onScheduledMealDelete(meal.id);
                                                                    setSelectedDayMeals(selectedDayMeals.filter(m => m.id !== meal.id));
                                                                    // Refresh sessions to update the calendar
                                                                    if (sessions.length === 0) {
                                                                        fetchSessions();
                                                                    }
                                                                } catch (err) {
                                                                    console.error('Error deleting scheduled meal:', err);
                                                                }
                                                            }
                                                        }}
                                                        className="text-xs font-mono uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity px-2 py-1"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            </div>
                                            <Heading level={4} className="font-mono uppercase tracking-wider text-base text-neutral-900">
                                                {meal.recipe_name}
                                            </Heading>
                                            {meal.meal_type && (
                                                <Text className="text-xs text-neutral-900 opacity-60 capitalize mt-1">
                                                    {meal.meal_type}
                                                </Text>
                                            )}
                                            {meal.cooking_time && (
                                                <Text className="font-mono text-xs text-neutral-900 opacity-60 mt-1">
                                                    {meal.cooking_time}m
                                                </Text>
                                            )}
                                        </div>
                                        <div className="flex space-x-2 mt-3">
                                            <button
                                                onClick={() => navigate(`/recipes/${meal.recipe_id}`)}
                                                className="px-3 py-1 text-xs font-mono uppercase tracking-wider bg-[#1b263b] text-white hover:opacity-60 transition-opacity"
                                            >
                                                VIEW RECIPE
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                    
                    {/* Cooking Sessions Section */}
                    {selectedDaySessions.length > 0 && (
                        <>
                            <Heading level={3} className="mb-4 font-mono uppercase tracking-wider text-sm">
                                COOKING SESSIONS
                            </Heading>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {selectedDaySessions.map((session, index) => (
                            <div key={session.id} className={`border p-4 ${
                                session.was_completed 
                                    ? 'bg-neutral-900 text-neutral-0 border-neutral-700' 
                                    : session.is_paused
                                    ? 'bg-yellow-50 border-yellow-200'
                                    : 'bg-neutral-50 border-neutral-200'
                            }`}>
                                <div className="mb-4">
                                    <div className="mb-3">
                                        <div className="w-full mb-3">
                                            {(() => {
                                                // Priority: in-progress photos > recipe images > base images > default
                                                let imageUrl = null;
                                                
                                                // Check for in-progress photos first
                                                if (session.in_progress_photos && session.in_progress_photos.length > 0) {
                                                    imageUrl = session.in_progress_photos[0];
                                                }
                                                // Check for recipe images
                                                else if (session.recipe_images && session.recipe_images.length > 0) {
                                                    imageUrl = session.recipe_images[0];
                                                }
                                                // Check for base images
                                                else if (session.base_image_url) {
                                                    imageUrl = session.base_image_url;
                                                }
                                                
                                                return imageUrl ? (
                                                    <img 
                                                        src={imageUrl} 
                                                        alt="Recipe" 
                                                        className="w-full h-24 object-cover rounded-sm"
                                                    />
                                                ) : (
                                                    <div className={`w-full h-24 rounded-sm flex items-center justify-center text-2xl font-mono ${
                                                        session.was_completed 
                                                            ? 'bg-neutral-700 text-neutral-0' 
                                                            : session.is_paused
                                                            ? 'bg-neutral-900 text-neutral-0'
                                                            : 'bg-neutral-200 text-neutral-900'
                                                    }`}>
                                                        {session.recipe_name ? session.recipe_name.charAt(0).toUpperCase() : 
                                                         session.kernel_title ? session.kernel_title.charAt(0).toUpperCase() : '?'}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                        <div>
                                            <Heading level={4} className={`font-mono uppercase tracking-wider text-base ${
                                                session.was_completed ? 'text-neutral-0' : 'text-neutral-900'
                                            }`}>
                                                {session.recipe_name || session.kernel_title || 'Custom Recipe'}
                                            </Heading>
                                            <div className={`text-xs font-mono uppercase tracking-wider mt-1 ${
                                                session.was_completed 
                                                    ? 'text-neutral-300' 
                                                    : session.is_paused
                                                    ? 'text-yellow-700'
                                                    : 'text-neutral-600'
                                            }`}>
                                                {session.was_completed ? 'COMPLETED' : session.is_paused ? 'PAUSED' : 'IN PROGRESS'}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Action Buttons - Below Content */}
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() => {
                                                setSelectedSession(session);
                                                setSessionDetailModalOpen(true);
                                                setDaySessionsModalOpen(false);
                                            }}
                                            className={`px-3 py-1 text-xs font-mono uppercase tracking-wider hover:opacity-70 transition-opacity ${
                                                session.was_completed 
                                                    ? 'bg-neutral-700 text-neutral-0' 
                                                    : session.is_paused
                                                    ? 'bg-yellow-600 text-neutral-0'
                                                    : 'bg-neutral-900 text-neutral-0'
                                            }`}
                                        >
                                            DETAILS
                                        </button>
                                        {!session.was_completed && (
                                            <button
                                                onClick={() => {
                                                    onSessionClick(session);
                                                    setDaySessionsModalOpen(false);
                                                }}
                                                className={`px-3 py-1 text-xs font-mono uppercase tracking-wider hover:opacity-70 transition-opacity ${
                                                    session.is_paused
                                                        ? 'bg-green-600 text-neutral-0'
                                                        : 'bg-neutral-900 text-neutral-0'
                                                }`}
                                            >
                                                {session.is_paused ? 'RESUME' : 'CONTINUE'}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => {
                                                onSessionDelete(session.id);
                                                setDaySessionsModalOpen(false);
                                            }}
                                            className="px-3 py-1 text-xs font-mono uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
                                        >
                                            DELETE
                                        </button>
                                    </div>
                                </div>
                                
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center space-x-2">
                                        <span className={`opacity-60 ${
                                            session.was_completed ? 'text-neutral-300' : 'text-neutral-900'
                                        }`}>Duration:</span>
                                        <span className={`font-mono ${
                                            session.was_completed ? 'text-neutral-0' : 'text-neutral-900'
                                        }`}>
                                            {session.actual_duration_seconds ? 
                                                `${formatDuration(session.actual_duration_seconds)}` : 
                                                `${session.total_duration_minutes}m`
                                            }
                                        </span>
                                    </div>
                                    {session.user_goal && (
                                        <div className="flex items-start space-x-2">
                                            <span className={`opacity-60 ${
                                                session.was_completed ? 'text-neutral-300' : 'text-neutral-900'
                                            }`}>Goal:</span>
                                            <span className={`font-mono flex-1 line-clamp-2 ${
                                                session.was_completed ? 'text-neutral-0' : 'text-neutral-900'
                                            }`}>
                                                {session.user_goal}
                                            </span>
                                        </div>
                                    )}
                                    {(session.reflection_comments || session.reflection_ingredient_notes || session.reflection_improvement_notes) && (
                                        <div className={`text-xs ${
                                            session.was_completed ? 'text-neutral-300' : 'text-neutral-900 opacity-60'
                                        }`}>
                                            Has reflection notes
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                            </div>
                        </>
                    )}
                </div>
            </Modal>

            {/* Session Detail Modal */}
            <Modal
                isOpen={sessionDetailModalOpen}
                onClose={() => {
                    setSessionDetailModalOpen(false);
                    setSelectedSession(null);
                }}
                title="Session Details"
                maxWidth="max-w-3xl"
            >
                {selectedSession && (
                    <div className="vspace">
                        {/* Session Info */}
                        <div className="mb-6">
                            <div className="flex items-start space-x-4 mb-4">
                                <div className="w-16 h-24 flex-shrink-0">
                                    {(() => {
                                        // Priority: in-progress photos > recipe images > base images > default
                                        let imageUrl = null;
                                        
                                        // Check for in-progress photos first
                                        if (selectedSession.in_progress_photos && selectedSession.in_progress_photos.length > 0) {
                                            imageUrl = selectedSession.in_progress_photos[0];
                                        }
                                        // Check for recipe images
                                        else if (selectedSession.recipe_images && selectedSession.recipe_images.length > 0) {
                                            imageUrl = selectedSession.recipe_images[0];
                                        }
                                        // Check for base images
                                        else if (selectedSession.base_image_url) {
                                            imageUrl = selectedSession.base_image_url;
                                        }
                                        
                                        return imageUrl ? (
                                            <img 
                                                src={imageUrl} 
                                                alt="Recipe" 
                                                className="w-16 h-24 object-cover rounded-sm"
                                            />
                                        ) : (
                                            <div className={`w-16 h-24 rounded-sm flex items-center justify-center text-2xl font-mono ${
                                                selectedSession.was_completed 
                                                    ? 'bg-neutral-700 text-neutral-0' 
                                                    : selectedSession.is_paused
                                                    ? 'bg-neutral-900 text-neutral-0'
                                                    : 'bg-neutral-200 text-neutral-900'
                                            }`}>
                                                {selectedSession.recipe_name ? selectedSession.recipe_name.charAt(0).toUpperCase() : 
                                                 selectedSession.kernel_title ? selectedSession.kernel_title.charAt(0).toUpperCase() : '?'}
                                            </div>
                                        );
                                    })()}
                                </div>
                                <div className="flex-1">
                                    <Heading level={4} className="mb-2 font-mono uppercase tracking-wider">
                                        {selectedSession.recipe_name || selectedSession.kernel_title || 'Custom Recipe'}
                                    </Heading>
                                    <div className={`text-sm font-mono uppercase tracking-wider ${
                                        selectedSession.was_completed 
                                            ? 'text-neutral-600' 
                                            : selectedSession.is_paused
                                            ? 'text-yellow-700'
                                            : 'text-neutral-900'
                                    }`}>
                                        {selectedSession.was_completed ? 'COMPLETED' : selectedSession.is_paused ? 'PAUSED' : 'IN PROGRESS'}
                                    </div>
                                </div>
                            </div>
                            <div className="bg-neutral-50 border border-neutral-200 p-4">
                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center space-x-2">
                                        <span className="text-neutral-900 opacity-60">Date:</span>
                                        <span className="text-neutral-900 font-mono">
                                            {formatDate(new Date(selectedSession.created_at))}
                                        </span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <span className="text-neutral-900 opacity-60">Duration:</span>
                                        <span className="text-neutral-900 font-mono">
                                            {selectedSession.actual_duration_seconds ? 
                                                `${formatDuration(selectedSession.actual_duration_seconds)} (actual)` : 
                                                `${selectedSession.total_duration_minutes}m (estimated)`
                                            }
                                        </span>
                                    </div>
                                    {selectedSession.user_goal && (
                                        <div className="flex items-start space-x-2">
                                            <span className="text-neutral-900 opacity-60">Goal:</span>
                                            <span className="text-neutral-900 font-mono flex-1">
                                                {selectedSession.user_goal}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Reflection Data */}
                        {selectedSession.reflection_comments && (
                            <div className="mb-6">
                                <Heading level={4} className="mb-3 font-mono uppercase tracking-wider">Comments</Heading>
                                <div className="bg-neutral-50 border border-neutral-200 p-4">
                                    <Text className="leading-relaxed">{selectedSession.reflection_comments}</Text>
                                </div>
                            </div>
                        )}

                        {selectedSession.reflection_ingredient_notes && (
                            <div className="mb-6">
                                <Heading level={4} className="mb-3 font-mono uppercase tracking-wider">Ingredient Notes</Heading>
                                <div className="bg-neutral-50 border border-neutral-200 p-4">
                                    <Text className="leading-relaxed">{selectedSession.reflection_ingredient_notes}</Text>
                                </div>
                            </div>
                        )}

                        {selectedSession.reflection_improvement_notes && (
                            <div className="mb-6">
                                <Heading level={4} className="mb-3 font-mono uppercase tracking-wider">Improvement Notes</Heading>
                                <div className="bg-neutral-50 border border-neutral-200 p-4">
                                    <Text className="leading-relaxed">{selectedSession.reflection_improvement_notes}</Text>
                                </div>
                            </div>
                        )}

                        {!selectedSession.reflection_comments && 
                         !selectedSession.reflection_ingredient_notes && 
                         !selectedSession.reflection_improvement_notes && (
                            <div className="text-center py-8">
                                <Text className="text-neutral-900 opacity-60">
                                    No reflections were saved for this session.
                                </Text>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="mt-6 pt-4 border-t border-neutral-200">
                            <div className="flex space-x-3">
                                {selectedSession.was_completed ? (
                                    <div className="flex-1">
                                        <Text className="text-sm text-neutral-900 opacity-60 mb-3">
                                            This session is completed. Start a new session to cook this recipe again.
                                        </Text>
                                        <button
                                            onClick={() => {
                                                // Navigate to start a new session - this would need to be passed as a prop
                                                setSessionDetailModalOpen(false);
                                                setSelectedSession(null);
                                            }}
                                            className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                                        >
                                            START NEW SESSION
                                        </button>
                                    </div>
                                ) : selectedSession.is_paused ? (
                                    <>
                                        <button
                                            onClick={() => {
                                                onSessionClick(selectedSession);
                                                setSessionDetailModalOpen(false);
                                                setSelectedSession(null);
                                            }}
                                            className="px-4 py-2 bg-green-600 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:bg-green-700 transition-colors"
                                        >
                                            RESUME SESSION
                                        </button>
                                        <button
                                            onClick={() => {
                                                onSessionDelete(selectedSession.id);
                                                setSessionDetailModalOpen(false);
                                                setSelectedSession(null);
                                            }}
                                            className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                        >
                                            DELETE
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            onClick={() => {
                                                onSessionClick(selectedSession);
                                                setSessionDetailModalOpen(false);
                                                setSelectedSession(null);
                                            }}
                                            className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                                        >
                                            CONTINUE SESSION
                                        </button>
                                        <button
                                            onClick={() => {
                                                onSessionDelete(selectedSession.id);
                                                setSessionDetailModalOpen(false);
                                                setSelectedSession(null);
                                            }}
                                            className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                        >
                                            DELETE
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Modal>
        </>
    );
};

export default CookingCalendar; 